<template>
    <label class="f fv pr ath-form-input" :for="uid">
        <div class="f fh fa-c dw-10 sm-b-xs" v-if="lbl">
            <slot name="labelLeft"></slot>
            <span class="fg ath-form-input-label">{{ uiTitle }}<slot name="labelAfter"></slot></span>
            <slot name="labelRight"></slot>
        </div>
        <!-- Input -->
        <input ref="input"
            novalidate
            class="dw-10"
            :class="[uiCounterEnabled ? 'el--withcounter' : '', uiReadonly ? 'c-na' : '']"
            @input="update"
            v-model="input_value"
            :type="type"
            :name="uid"
            :id="uid"
            :disabled="disabled"
            :placeholder="$options.filters.athT(placeholder)"
            :maxlength="maxlength"
            @focus="focusChange(true)"
            @blur="focusChange(false)" />
        <!-- Remaining Counter -->
        <template v-if="uiCounterEnabled">
            <small v-if="uiCounterVisible"
                class="pa-br ath-form-input-counter"
                :class="uiCounterCname">{{ uiCounterRemaining }}</small>
        </template>
    </label>
</template>

<script>
import { guuid, isNeString, isNumber, join } from '@aretheyhappy/utils';

export default {
    name: 'mead-form-input',
    props: {
        /**
         * Label i18n key rendered above the input. Only shown when truthy.
         */
        lbl             : {type: [String, Boolean], default: () => false},
        /**
         * Entity name interpolated into the label when translating it.
         */
        lblEntity       : {type: [Boolean, String], default: () => false},
        /**
         * Whether the interpolated entity name should be lowercased in the translated label.
         */
        lblEntityLc     : {type: Boolean, default: () => true},
        /**
         * Placeholder text (i18n key) shown in the input when empty.
         */
        placeholder     : {type: [String, Boolean], default: () => false},
        /**
         * Native `type` attribute of the input (e.g. 'text', 'password', 'email').
         */
        type            : {type: String, default: () => 'text'},
        /**
         * Current input value (v-model).
         */
        value           : {type: [String, Number], default: () => ''},
        /**
         * Disables the input.
         */
        disabled        : {type: Boolean, default: () => false},
        /**
         * Milliseconds to debounce the `input` emission by; `false` emits on every keystroke.
         */
        debounce        : {type: [Number, Boolean], default: () => false},
        /**
         * Function invoked when the input receives focus, attached as a native `focus` listener.
         */
        uiOnFocus       : {type: [Boolean, Function], default: () => false},
        /**
         * Automatically focuses the input once mounted.
         */
        uiAutoFocus     : {type: Boolean, default: () => false},
        /**
         * Appends an "(optional)" suffix to the label.
         */
        uiOptional      : {type: Boolean, default: () => false},
        /**
         * Marks the input as readonly.
         */
        uiReadonly      : {type: Boolean, default: () => false},
        /**
         * Whether the browser is allowed to autocomplete/autofill this input. When `false`, the input
         * is kept readonly until focused, to work around forced browser autofill.
         */
        uiAutocomplete  : {type: Boolean, default: () => true},
        /**
         * Maximum character length allowed; also enables the remaining-character counter.
         */
        maxlength       : {type: [Number, Boolean], default: () => false},
    },
    data () {
        return {
            uid                 : guuid(),
            ui_debounce         : isNumber(this.debounce) ? parseInt(this.debounce) : false,
            ui_debounce_timer   : false,
            input_value         : this.value,
        };
    },
    methods: {
        update: function () {
            if (this.ui_debounce === false) {
                this.$emit('input', this.input_value);
            } else {
                if (this.ui_debounce_timer) clearTimeout(this.ui_debounce_timer);
                this.ui_debounce_timer = setTimeout(
                    () => this.$emit('input', this.input_value),
                    this.ui_debounce
                );
            }
        },
        focusChange: function (focus) {
            this.$emit('focus', focus);
        },
    },
    computed: {
        uiTitle: function () {
            return join([
                this.lblEntity
                    ? MeadCommon.I18N[this.lblEntityLc ? 'actionLc' : 'action'](this.lbl, this.lblEntity)
                    : MeadCommon.I18N.go(this.lbl),
                this.uiOptional
                    ? `(${MeadCommon.I18N.go('mead.labels.optional')})`
                    : false,
            ], {delim: ' '});
        },
        uiCounterEnabled: function () {
            return isNumber(this.maxlength);
        },
        uiCounterVisible: function () {
            if (!this.uiCounterEnabled) return false;
            return this.uiCounterRemaining < (this.maxlength * 0.7);
        },
        uiCounterRemaining: function () {
            if (!this.uiCounterEnabled) return 0;
            if (!isNeString(this.input_value)) return this.maxlength;
            return this.maxlength - this.input_value.length;
        },
        uiCounterCname: function () {
            if (!this.uiCounterEnabled || this.uiCounterRemaining >= 10) return '';
            return this.uiCounterRemaining > 5 ? 'el--t-warning' : 'el--t-danger';
        },
    },
    watch: {
        value: function () {
            this.input_value = this.value;
        },
    },
    async mounted () {
        //  Set readonly if disableAutocomplete is passed
        if (!this.uiAutocomplete && !this.uiReadonly) {
            this.$refs.input.setAttribute('readonly', 'readonly');
            this.$refs.input.addEventListener('focus', () => this.$refs.input.removeAttribute('readonly'));
        }

        //  Set readonly if uiReadonly is passed
        if (this.$refs.input && this.uiReadonly) {
            this.$refs.input.setAttribute('readonly', 'readonly');
        }

        //  UIAutoFocus
        if (this.$refs.input && this.uiAutoFocus) {
            await this.$nextTick();
            if (this.type === 'password') {
                this.$refs.input.focus();
                this.$refs.input.click();
            } else {
                this.$refs.input.focus();
            }
        }

        //  Add uiOnFocus
        if (this.$refs.input && this.uiOnFocus) {
            this.$refs.input.addEventListener('focus', this.uiOnFocus);
        }
    },
};
</script>

<style lang="scss" scoped>
@use '../../../../sass/variables' as *;

input {
    font-family: $ff-b;
    font-weight: 400;
    font-size: $fs-s;
    height: 3rem;
    padding: 0 $s-s;
    border: none;
    border-radius: $br-m;
    line-height: initial;
    box-shadow: inset 0 0 4rem 4rem var(--c-brand-light-bg);
    background-color: var(--c-brand-light-bg);
    color: var(--c-brand-light-fg);

    &[disabled="disabled"] {
        cursor: not-allowed;
        opacity: .65;
    }

    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &:-internal-autofill-selected {
        color: var(--c-brand-light-fg) !important;
        -webkit-text-fill-color: var(--c-brand-light-fg) !important;
    }

    &:active,
    &:focus {
        border-color: transparent;
    }

    &.el--withcounter {
        padding-right: $s-l;
    }
}

.ath-form-input-label {
    font-family: $ff-b;
    font-weight: 400;
    line-height: $lh-xs;
    font-size: $fs-s;
    color: var(--c-txt);
}

.ath-form-input {
    &.el--invalid input {
        outline: 1px solid var(--c-brand-danger-bg);
    }
}

.ath-form-input-counter {
    font-weight: bold;
    font-size: $fs-xxs;
    line-height: $fs-xxs;
    color: var(--c-txt-light);
    background-color: #fff;
    padding-left: .2rem;
    padding-top: .2rem;

    &.el--t-warning {
        color: #ee9e2f;
    }

    &.el--t-danger {
        color: #ff0f0f;
    }
}

//  SIZES

@mixin inputSize ($name, $height) {
    .ath-form-input.el--s-#{$name} input {
        height: $height;
    }
}

@include inputSize('s', 3rem);
@include inputSize('m', 4rem);
@include inputSize('l', 5rem);
</style>
